/**
 * @file        src/components/ToastHost.jsx
 * @description App-wide toast. Mounted once next to <ModalsHost /> and driven by a
 *              window event, exactly like the modals are:
 *
 *                window.dispatchEvent(new CustomEvent('nh:toast', { detail: {
 *                  msg,            // required — the line shown
 *                  ms,             // optional — how long it stays (default 5s)
 *                  onDone,         // optional — run when it closes, either way
 *                } }))
 *
 *              `onDone` lets a caller put a message IN FRONT OF an action: raise the
 *              toast, hand over what should happen next, and it runs when the toast
 *              goes. Used by the flight search to show the concession notice before
 *              the search actually leaves the page.
 * @author      Kinetika Labs
 * @created     2026-08-12
 * @changelog
 *   - 2026-08-12 Claude — Added `ms` + `onDone` so a toast can DELAY the action it
 *                          describes. Pressing Search navigated instantly, so a
 *                          message raised at the same moment was read by nobody:
 *                          the results page was already painting over it. The search
 *                          now waits behind the toast.
 *   - 2026-08-12 Claude — Initial. Lives at the APP ROOT, and that is the whole
 *                          point of it. The first version of this toast was local
 *                          state inside FlightSearchForm, which never appeared: the
 *                          same click that raised it called navigate('/flights/results'),
 *                          the Home page unmounted, and the toast went with it about a
 *                          frame after mounting. Anything raised at the moment of a
 *                          route change has to outlive the component that raised it.
 */
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

const DEFAULT_MS = 5000;

export default function ToastHost() {
  // An OBJECT, not a string: `id` changes on every dispatch, which remounts the
  // toast so the timer and its countdown bar restart. A repeated identical message
  // would otherwise show a bar that had already run down.
  const [toast, setToast] = useState(null);

  // Closing ALWAYS runs the follow-up, whether the customer pressed ✕ or the timer
  // ran out. The caller has parked an action behind this toast — dropping it on one
  // of the two exits would mean a search the customer asked for silently never runs.
  const close = () => {
    if (!toast) return;
    const done = toast.onDone;
    setToast(null);
    if (typeof done === 'function') done();
  };

  useEffect(() => {
    const onToast = (e) => {
      const { msg, ms, onDone } = e?.detail || {};
      if (!msg) return;
      setToast({ msg, ms: Number(ms) > 0 ? Number(ms) : DEFAULT_MS, onDone, id: Date.now() });
    };
    window.addEventListener('nh:toast', onToast);
    return () => window.removeEventListener('nh:toast', onToast);
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(close, toast.ms);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toast]);

  if (!toast) return null;

  return createPortal(
    <div
      key={toast.id}
      role="status"
      aria-live="polite"
      className="fixed top-4 right-4 left-4 sm:left-auto sm:w-[24rem] z-[500] overflow-hidden rounded-xl border border-line bg-panel shadow-2xl"
    >
      <div className="flex items-start gap-3 p-4 pr-10">
        <span className="w-6 h-6 rounded-full bg-rose-500 text-white flex items-center justify-center shrink-0 text-[13px] font-bold leading-none">!</span>
        <p className="text-[13px] leading-relaxed text-ink">{toast.msg}</p>
      </div>
      <button
        type="button"
        onClick={close}
        aria-label="Dismiss"
        className="absolute top-2.5 right-2.5 w-6 h-6 rounded-md text-muted hover:text-ink flex items-center justify-center cursor-pointer"
      >
        <svg viewBox="0 0 24 24" className="w-4 h-4"><path fill="currentColor" d="M18.3 5.71 12 12l6.3 6.29-1.42 1.42L10.59 13.4 4.29 19.7 2.88 18.3 9.17 12 2.88 5.71 4.29 4.3l6.3 6.29 6.29-6.3z" /></svg>
      </button>
      {/* Counts down the time the toast is on screen (see .nh-toast-bar). The
          duration is set here, not in the CSS, so the bar can never disagree with
          the timer that actually closes it. */}
      <span className="nh-toast-bar block h-1 bg-rose-500" style={{ animationDuration: `${toast.ms}ms` }} />
    </div>,
    document.body,
  );
}
